// 🔧 marketing_pages.js
// ✅ Purpose: Central content and SEO configuration for public marketing pages
// ✅ Features:
// - Keeps SEO titles/descriptions in one location
// - Keeps industry page content in one reusable structure
// - Supports reusable landing page rendering
// - Makes future marketing/SEO pages easier to add and maintain

export const MARKETING_PAGES = {
  carShows: {
    path: "/car-shows",
    seoTitle: "QR Code Voting for Car Shows | Let's Go Vote",
    seoDescription:
      "Simple QR code voting for car shows, People's Choice awards, Best in Show contests, custom categories, and live leaderboards.",
    seoKeywords:
      "car show voting, QR code voting for car shows, people's choice car show voting, best in show voting",
    eyebrow: "Car Show Voting",
    title: "QR Code Voting for Car Shows",
    subtitle:
      "Let fans vote for their favorite cars by category, Best in Show, or custom awards using simple QR-code ballots.",
    features: [
      {
        title: "People's Choice Voting",
        description: "Let attendees vote for their favorite entries from their phones.",
      },
      {
        title: "Best in Show",
        description: "Support top-level awards across the full event.",
      },
      {
        title: "Category Voting",
        description: "Organize vehicles by class, year, style, or event-specific categories.",
      },
      {
        title: "Live Leaderboards",
        description: "Show results during or after voting when you're ready.",
      },
      {
        title: "QR Code Entry Links",
        description: "Print QR cards for individual vehicles or full event voting.",
      },
      {
        title: "Manual Voting Support",
        description: "Admins can help guests who need assistance submitting votes.",
      },
    ],
    idealFor: [
      "Classic car shows",
      "VW shows",
      "Hot rod events",
      "Cruise-ins",
      "Motorcycle shows",
      "Best in Show awards",
    ],
    faqs: [
      {
        question: "Do voters need to download an app?",
        answer:
          "No. Voters scan a QR code and vote from a mobile-friendly web page.",
      },
      {
        question: "Can I use categories for different vehicle classes?",
        answer:
          "Yes. Events can use custom categories such as class, year, style, or award type.",
      },
      {
        question: "Can results be hidden until voting closes?",
        answer:
          "Yes. Leaderboards and results can be controlled from the event settings.",
      },
    ],
    relatedPages: ["qrCodeVoting", "peoplesChoiceVoting", "sponsorManagement", "volunteerManagement"],
        socialImage: "https://letsgovote.app/images/social-share-car-shows.png",

  },

  artShows: {
    path: "/art-shows",
    seoTitle: "Voting for Art Shows and Exhibitions | Let's Go Vote",
    seoDescription:
      "Simple mobile voting and judged scorecards for art shows, galleries, school contests, exhibitions, and public choice awards.",
    seoKeywords:
      "art show voting, public choice art voting, judged scorecards, art exhibition voting",
    eyebrow: "Art Show Voting",
    title: "Simple Voting for Art Shows and Exhibitions",
    subtitle:
      "Let visitors vote for favorite pieces, support judged scorecards, and make award selection easier for galleries, schools, and community events.",
    features: [
      {
        title: "Public Choice Awards",
        description: "Let guests vote for their favorite artwork from a mobile ballot.",
      },
      {
        title: "Judged Scorecards",
        description: "Use scoring categories when events need structured judging.",
      },
      {
        title: "Artwork Categories",
        description: "Group entries by medium, age group, theme, or division.",
      },
      {
        title: "Easy QR Access",
        description: "Place QR codes near displays or at event entrances.",
      },
      {
        title: "Clean Results",
        description: "Review vote totals and scoring details from the admin area.",
      },
      {
        title: "Flexible Setup",
        description: "Use simple voting or detailed judging depending on the event.",
      },
    ],
    idealFor: [
      "Community art shows",
      "School art contests",
      "Gallery exhibitions",
      "Photography contests",
      "Maker fairs",
      "Juried art events",
    ],
    faqs: [
      {
        question: "Can visitors vote for their favorite artwork?",
        answer:
          "Yes. Public choice voting lets guests vote from their phones using QR codes.",
      },
      {
        question: "Can judges use scorecards?",
        answer:
          "Yes. Judged scorecards can support structured scoring for more formal events.",
      },
      {
        question: "Can entries be grouped by category?",
        answer:
          "Yes. You can organize entries by medium, age group, theme, or division.",
      },
    ],
    socialImage: "https://letsgovote.app/images/social-share-art-shows.png",
    relatedPages: ["qrCodeVoting", "peoplesChoiceVoting", "judgedScorecards", "sponsorManagement"],
  },

  petShows: {
    path: "/pet-shows",
    seoTitle: "Voting for Pet Shows and Pet Contests | Let's Go Vote",
    seoDescription:
      "Fun QR code voting for pet shows, pet costume contests, cutest pet awards, best trick contests, and crowd favorite voting.",
    seoKeywords:
      "pet show voting, pet contest voting, QR code pet voting, cutest pet contest voting",
    eyebrow: "Pet Show Voting",
    title: "Fun and Easy Voting for Pet Shows",
    subtitle:
      "Let attendees vote for cutest, funniest, best costume, crowd favorite, and other pet show awards from their phones.",
    features: [
      {
        title: "Crowd Favorite Voting",
        description: "Let guests vote quickly using QR codes.",
      },
      {
        title: "Custom Categories",
        description: "Create categories like Best Costume, Cutest Pet, or Best Trick.",
      },
      {
        title: "Mobile Ballots",
        description: "No paper ballots or manual counting needed.",
      },
      {
        title: "Photo-Friendly Entries",
        description: "Use entry photos to help voters identify pets.",
      },
      {
        title: "Live or Hidden Results",
        description: "Choose when leaderboards and results should be visible.",
      },
      {
        title: "Volunteer Friendly",
        description: "Easy enough for event helpers to support during check-in.",
      },
    ],
    idealFor: [
      "Pet costume contests",
      "Dog shows",
      "Shelter fundraisers",
      "Community pet events",
      "Best trick contests",
      "Pet photo contests",
    ],
    faqs: [
      {
        question: "Can we create fun award categories?",
        answer:
          "Yes. You can create categories like Best Costume, Cutest Pet, Best Trick, or Crowd Favorite.",
      },
      {
        question: "Can voters see pet photos?",
        answer:
          "Yes. Entries can include photos to help voters identify each pet.",
      },
      {
        question: "Is this easy for families and guests?",
        answer:
          "Yes. Guests scan a QR code and vote from a simple mobile ballot.",
      },
    ],
    relatedPages: ["qrCodeVoting", "peoplesChoiceVoting", "volunteerManagement"],
    socialImage: "https://letsgovote.app/images/social-share-pet-shows.png",
  },

  schoolElections: {
    path: "/school-elections",
    seoTitle: "Online Voting for School Elections | Let's Go Vote",
    seoDescription:
      "Simple online voting for student council elections, club elections, classroom decisions, school awards, and ranked choice ballots.",
    seoKeywords:
      "school election voting, student council voting, ranked choice school election, online voting for schools",
    eyebrow: "School Election Voting",
    title: "Simple Online Voting for School Elections",
    subtitle:
      "Support student elections, club votes, classroom decisions, and ranked choice voting with a clean QR-code voting experience.",
    features: [
      {
        title: "Ranked Choice Voting",
        description: "Let voters rank candidates in order of preference.",
      },
      {
        title: "Candidate Categories",
        description: "Support offices like President, Treasurer, Secretary, or class roles.",
      },
      {
        title: "Controlled Access",
        description: "Use account-based voting to help prevent duplicate votes.",
      },
      {
        title: "Clear Ballots",
        description: "Give students a simple mobile-friendly voting experience.",
      },
      {
        title: "Auditing Support",
        description: "Review voting activity and results with admin tools.",
      },
      {
        title: "Fast Setup",
        description: "Create candidates, categories, and voting windows quickly.",
      },
    ],
    idealFor: [
      "Student council elections",
      "Club officer elections",
      "Classroom voting",
      "School award voting",
      "PTA or booster decisions",
      "Ranked choice practice elections",
    ],
    faqs: [
      {
        question: "Can this support ranked choice voting?",
        answer:
          "Yes. Ranked choice voting lets voters rank candidates in order of preference.",
      },
      {
        question: "Can voting be limited to registered users?",
        answer:
          "Yes. Account-based voting helps reduce duplicate votes.",
      },
      {
        question: "Can admins review results and activity?",
        answer:
          "Yes. Admin tools support result review and audit workflows.",
      },
    ],
    relatedPages: ["rankedChoiceVoting", "qrCodeVoting", "volunteerManagement"],
    socialImage: "https://letsgovote.app/images/social-share-school-elections.png",
  },

  silentAuctions: {
    path: "/silent-auctions",
    seoTitle: "QR Code Silent Auctions for Fundraisers | Let's Go Vote",
    seoDescription:
      "Run QR code silent auctions with item pages, mobile bidding, auction timers, outbid notices, and winner review tools.",
    seoKeywords:
      "QR code silent auction, silent auction app, fundraiser auction software, mobile bidding",
    eyebrow: "Silent Auction Tools",
    title: "QR Code Silent Auctions for Fundraisers and Events",
    subtitle:
      "List auction items, let guests bid from their phones, and review winners when the auction closes.",
    features: [
      {
        title: "Auction Item Pages",
        description: "Create standalone auction items with details, images, and starting bids.",
      },
      {
        title: "QR Code Bidding",
        description: "Guests can scan item QR codes and place bids from their phones.",
      },
      {
        title: "Auction Timer",
        description: "Control when bidding opens and closes.",
      },
      {
        title: "Outbid Notices",
        description: "Notify bidders when they have been outbid.",
      },
      {
        title: "Winner Review",
        description: "Review winning bids from the admin auction results page.",
      },
      {
        title: "Event Add-On",
        description: "Use auctions alongside voting, sponsors, volunteers, or event entries.",
      },
    ],
    idealFor: [
      "School fundraisers",
      "Nonprofit auctions",
      "Car show fundraisers",
      "Community events",
      "Benefit nights",
      "Club fundraising events",
    ],
    faqs: [
      {
        question: "Can guests bid from their phones?",
        answer:
          "Yes. Guests can scan an item QR code and place bids from a mobile page.",
      },
      {
        question: "Can the auction have a closing time?",
        answer:
          "Yes. Auction timers help control when bidding opens and closes.",
      },
      {
        question: "Can admins review winners?",
        answer:
          "Yes. Admin auction results help review winning bids after bidding closes.",
      },
    ],
    relatedPages: ["qrCodeVoting", "sponsorManagement", "volunteerManagement"],
    socialImage: "https://letsgovote.app/images/social-share-silent-auctions.png",
  },
  qrCodeVoting: {
    path: "/qr-code-voting",
    seoTitle: "QR Code Voting for Events | Let's Go Vote",
    seoDescription:
      "Simple QR code voting for events, contests, awards, elections, silent auctions, and community voting.",
    seoKeywords:
      "QR code voting, QR voting app, vote by QR code, event voting software",
    eyebrow: "QR Code Voting",
    title: "QR Code Voting for Real-World Events",
    subtitle:
      "Let guests scan, vote, bid, or participate from their phones without downloading an app.",
    features: [
      {
        title: "No App Downloads",
        description: "Guests use a mobile-friendly web page from a QR code.",
      },
      {
        title: "Event-Specific Links",
        description: "Send voters directly to ballots, entries, auction items, or public pages.",
      },
      {
        title: "Printable QR Codes",
        description: "Create QR cards for entries, items, or full event voting.",
      },
      {
        title: "Simple for Guests",
        description: "A scan-to-vote flow helps reduce confusion at busy events.",
      },
      {
        title: "Flexible Use Cases",
        description: "Use QR codes for voting, auctions, sponsors, volunteers, and event information.",
      },
      {
        title: "Admin Controlled",
        description: "Event managers control voting windows, visibility, and results.",
      },
    ],
    idealFor: [
      "Car shows",
      "Art shows",
      "Pet contests",
      "School elections",
      "Silent auctions",
      "Community events",
    ],
    faqs: [
      {
        question: "Do voters need to download an app?",
        answer: "No. Voters scan a QR code and use a mobile-friendly web page.",
      },
      {
        question: "Can each entry have its own QR code?",
        answer: "Yes. Events can use QR codes for individual entries, auction items, or full ballots.",
      },
      {
        question: "Can QR codes be printed before the event?",
        answer: "Yes. Admins can print QR pages for event setup and day-of use.",
      },
    ],
    relatedPages: ["carShows", "peoplesChoiceVoting", "silentAuctions"],
    socialImage: "https://letsgovote.app/images/social-share-qr-code-voting.png",
  },

  peoplesChoiceVoting: {
    path: "/peoples-choice-voting",
    seoTitle: "People's Choice Voting Software | Let's Go Vote",
    seoDescription:
      "Run People's Choice voting for events, awards, car shows, art shows, pet contests, and community competitions.",
    seoKeywords:
      "people's choice voting software, people's choice awards voting, public voting app",
    eyebrow: "People's Choice Voting",
    title: "People's Choice Voting Made Simple",
    subtitle:
      "Let attendees vote for their favorite entries using easy mobile ballots and QR-code access.",
    features: [
      {
        title: "Public Voting",
        description: "Guests can vote for favorites from their phones.",
      },
      {
        title: "Category Support",
        description: "Create categories for awards, divisions, classes, or contest groups.",
      },
      {
        title: "Best in Show",
        description: "Support overall awards in addition to category winners.",
      },
      {
        title: "Leaderboards",
        description: "Choose whether results are shown during or after voting.",
      },
      {
        title: "Manual Vote Help",
        description: "Admins can assist voters who need help submitting a vote.",
      },
      {
        title: "Event Controls",
        description: "Manage voting windows, ballot access, and result visibility.",
      },
    ],
    idealFor: [
      "People's Choice awards",
      "Car show voting",
      "Art show voting",
      "Pet contests",
      "Community competitions",
      "Audience favorite awards",
    ],
    faqs: [
      {
        question: "Can voters choose one favorite per category?",
        answer: "Yes. People's Choice voting works well for category-based favorite voting.",
      },
      {
        question: "Can I run Best in Show voting?",
        answer: "Yes. Best in Show can be enabled for events that need an overall winner.",
      },
      {
        question: "Can results stay hidden?",
        answer: "Yes. Admins can control when leaderboards and results are visible.",
      },
    ],
    relatedPages: ["carShows", "artShows", "petShows", "qrCodeVoting"],
    socialImage: "https://letsgovote.app/images/social-share-peoples-choice-voting.png",
  },

  rankedChoiceVoting: {
    path: "/ranked-choice-voting",
    seoTitle: "Ranked Choice Voting Tool for Events | Let's Go Vote",
    seoDescription:
      "Use ranked choice voting for school elections, club elections, awards, and events where voters rank candidates or choices.",
    seoKeywords:
      "ranked choice voting tool, ranked choice voting app, school election voting",
    eyebrow: "Ranked Choice Voting",
    title: "Ranked Choice Voting for Schools, Clubs, and Events",
    subtitle:
      "Let voters rank their choices in order of preference with a simple mobile-friendly ballot.",
    features: [
      {
        title: "Ranked Ballots",
        description: "Voters can rank candidates or options by preference.",
      },
      {
        title: "Multiple Categories",
        description: "Support offices, awards, roles, or grouped decisions.",
      },
      {
        title: "Mobile Friendly",
        description: "Voters use a clean ballot designed for phones.",
      },
      {
        title: "Controlled Access",
        description: "Use account-based voting to help reduce duplicate submissions.",
      },
      {
        title: "Audit Support",
        description: "Review vote activity and ballot records from admin tools.",
      },
      {
        title: "Flexible Setup",
        description: "Use ranked choice for elections, awards, or preference voting.",
      },
    ],
    idealFor: [
      "Student elections",
      "Club officer elections",
      "Board selections",
      "Preference voting",
      "Award voting",
      "Classroom elections",
    ],
    faqs: [
      {
        question: "What is ranked choice voting?",
        answer: "Ranked choice voting lets voters rank options in order of preference instead of choosing only one.",
      },
      {
        question: "Can this be used for school elections?",
        answer: "Yes. Ranked choice voting works well for student councils, clubs, and classroom decisions.",
      },
      {
        question: "Can admins review ballots?",
        answer: "Yes. Admin tools support reviewing results and voting activity.",
      },
    ],
    relatedPages: ["schoolElections", "qrCodeVoting"],
    socialImage: "https://letsgovote.app/images/social-share-ranked-choice-voting.png",
  },

  judgedScorecards: {
    path: "/judged-scorecards",
    seoTitle: "Judged Scorecards for Events | Let's Go Vote",
    seoDescription:
      "Use judged scorecards for contests, competitions, art shows, car shows, talent events, and structured event scoring.",
    seoKeywords:
      "judged scorecards, event scoring app, contest judging software, scorecard voting",
    eyebrow: "Judged Scorecards",
    title: "Structured Scorecards for Judged Events",
    subtitle:
      "Give judges a clean way to score entries using categories, factors, and event-specific judging criteria.",
    features: [
      {
        title: "Scoring Factors",
        description: "Create judging criteria that match your event.",
      },
      {
        title: "Judge-Friendly Forms",
        description: "Judges can enter scores from a simple mobile-friendly page.",
      },
      {
        title: "Category Support",
        description: "Group entries by division, class, or contest type.",
      },
      {
        title: "Detailed Results",
        description: "Review scoring totals and judging details from admin tools.",
      },
      {
        title: "Flexible Scoring",
        description: "Support simple points or more detailed scorecard workflows.",
      },
      {
        title: "Great for Formal Events",
        description: "Use scorecards when public voting is not enough.",
      },
    ],
    idealFor: [
      "Judged car shows",
      "Art competitions",
      "Talent contests",
      "School competitions",
      "Panel judging",
      "Formal award scoring",
    ],
    faqs: [
      {
        question: "Can judges score multiple factors?",
        answer: "Yes. Events can use scoring factors to support structured judging.",
      },
      {
        question: "Can scorecards work by category?",
        answer: "Yes. Scorecards can be used across categories, divisions, or classes.",
      },
      {
        question: "Can admins review scoring details?",
        answer: "Yes. Admins can review scoring results and judging activity.",
      },
    ],
    relatedPages: ["artShows", "carShows"],
    socialImage: "https://letsgovote.app/images/social-share-judged-scorecards.png",
  },

  volunteerManagement: {
    path: "/volunteer-management",
    seoTitle: "Volunteer Management for Events | Let's Go Vote",
    seoDescription:
      "Organize event volunteers, activities, time slots, assignments, and volunteer opportunities for community events.",
    seoKeywords:
      "volunteer management for events, volunteer signup software, event volunteer scheduling",
    eyebrow: "Volunteer Management",
    title: "Volunteer Signups and Scheduling for Events",
    subtitle:
      "Create volunteer activities, time slots, and opportunities so helpers know where they are needed.",
    features: [
      {
        title: "Activities and Time Slots",
        description: "Organize volunteer needs by role, activity, and schedule.",
      },
      {
        title: "Volunteer Opportunities",
        description: "Show public opportunities so people can sign up for available slots.",
      },
      {
        title: "Admin Assignment",
        description: "Managers can assign volunteers and review coverage.",
      },
      {
        title: "Event-Specific Setup",
        description: "Volunteer tools are scoped to each event.",
      },
      {
        title: "Print and Export Support",
        description: "Use volunteer lists for day-of coordination.",
      },
      {
        title: "Built Alongside Voting",
        description: "Keep voting, volunteers, sponsors, and event tools together.",
      },
    ],
    idealFor: [
      "Car shows",
      "School events",
      "Community fundraisers",
      "Pet shows",
      "Art shows",
      "Club events",
    ],
    faqs: [
      {
        question: "Can volunteers sign up for time slots?",
        answer: "Yes. Events can use activities and time slots for volunteer coordination.",
      },
      {
        question: "Can admins assign volunteers manually?",
        answer: "Yes. Managers can add or assign volunteers when needed.",
      },
      {
        question: "Can volunteer tools be used with voting?",
        answer: "Yes. Volunteer management can be used alongside voting and other event tools.",
      },
    ],
    relatedPages: ["carShows", "petShows", "silentAuctions"],
    socialImage: "https://letsgovote.app/images/social-share-volunteer-management.png",
  },

  sponsorManagement: {
    path: "/sponsor-management",
    seoTitle: "Sponsor Management for Events | Let's Go Vote",
    seoDescription:
      "Showcase event sponsors with sponsor pages, levels, logos, links, and event-specific sponsor visibility.",
    seoKeywords:
      "event sponsor management, sponsor pages for events, sponsor management software",
    eyebrow: "Sponsor Management",
    title: "Sponsor Pages and Visibility for Events",
    subtitle:
      "Give sponsors a cleaner presence with event sponsor pages, levels, links, and public visibility.",
    features: [
      {
        title: "Sponsor Levels",
        description: "Group sponsors by level so top sponsors receive proper visibility.",
      },
      {
        title: "Sponsor Pages",
        description: "Create public sponsor views with details and links.",
      },
      {
        title: "Logo Support",
        description: "Show sponsor branding where it matters.",
      },
      {
        title: "Event-Specific Sponsors",
        description: "Keep sponsor data connected to the event it supports.",
      },
      {
        title: "Public Sponsor Directory",
        description: "Let visitors browse event sponsors from public pages.",
      },
      {
        title: "Works With Voting",
        description: "Use sponsor visibility alongside ballots, QR links, and event pages.",
      },
    ],
    idealFor: [
      "Sponsored car shows",
      "School fundraisers",
      "Community events",
      "Silent auctions",
      "Club events",
      "Benefit events",
    ],
    faqs: [
      {
        question: "Can sponsors be grouped by level?",
        answer: "Yes. Sponsor levels help organize and highlight sponsors appropriately.",
      },
      {
        question: "Can sponsors have public pages?",
        answer: "Yes. Sponsor pages can show sponsor details, logos, and links.",
      },
      {
        question: "Can sponsor tools be used with voting events?",
        answer: "Yes. Sponsor management works alongside the voting and event tools.",
      },
    ],
    relatedPages: ["carShows", "silentAuctions", "artShows"],
    socialImage: "https://letsgovote.app/images/social-share-sponsor-management.png",

  },  
};

export const MARKETING_PAGE_LIST = [
  {
    group: "Event Types",
    pages: [
      {
        key: "carShows",
        title: "🚗 Car Shows",
        description: MARKETING_PAGES.carShows.seoDescription,
        path: "/car-shows",
      },
      {
        key: "artShows",
        title: "🎨 Art Shows",
        description: MARKETING_PAGES.artShows.seoDescription,
        path: "/art-shows",
      },
      {
        key: "petShows",
        title: "🐶 Pet Shows",
        description: MARKETING_PAGES.petShows.seoDescription,
        path: "/pet-shows",
      },
      {
        key: "schoolElections",
        title: "🗳️ School Elections",
        description: MARKETING_PAGES.schoolElections.seoDescription,
        path: "/school-elections",
      },
      {
        key: "silentAuctions",
        title: "🏷️ Silent Auctions",
        description: MARKETING_PAGES.silentAuctions.seoDescription,
        path: "/silent-auctions",
      },
    ],
  },
  {
    group: "Features",
    pages: [
      {
        key: "qrCodeVoting",
        title: "📱 QR Code Voting",
        description: MARKETING_PAGES.qrCodeVoting.seoDescription,
        path: "/qr-code-voting",
      },
      {
        key: "peoplesChoiceVoting",
        title: "🏆 People&apos;s Choice Voting",
        description: MARKETING_PAGES.peoplesChoiceVoting.seoDescription,
        path: "/peoples-choice-voting",
      },
      {
        key: "rankedChoiceVoting",
        title: "🗳️ Ranked Choice Voting",
        description: MARKETING_PAGES.rankedChoiceVoting.seoDescription,
        path: "/ranked-choice-voting",
      },
      {
        key: "judgedScorecards",
        title: "🧮 Judged Scorecards",
        description: MARKETING_PAGES.judgedScorecards.seoDescription,
        path: "/judged-scorecards",
      },
      {
        key: "volunteerManagement",
        title: "🤝 Volunteer Management",
        description: MARKETING_PAGES.volunteerManagement.seoDescription,
        path: "/volunteer-management",
      },
      {
        key: "sponsorManagement",
        title: "⭐ Sponsor Management",
        description: MARKETING_PAGES.sponsorManagement.seoDescription,
        path: "/sponsor-management",
      },
    ],
  },
];

export const MARKETING_PAGE_LINKS = {
  carShows: {
    title: "Car Shows",
    path: "/car-shows",
  },
  artShows: {
    title: "Art Shows",
    path: "/art-shows",
  },
  petShows: {
    title: "Pet Shows",
    path: "/pet-shows",
  },
  schoolElections: {
    title: "School Elections",
    path: "/school-elections",
  },
  silentAuctions: {
    title: "Silent Auctions",
    path: "/silent-auctions",
  },
  qrCodeVoting: {
    title: "QR Code Voting",
    path: "/qr-code-voting",
  },
  peoplesChoiceVoting: {
    title: "People's Choice Voting",
    path: "/peoples-choice-voting",
  },
  rankedChoiceVoting: {
    title: "Ranked Choice Voting",
    path: "/ranked-choice-voting",
  },
  judgedScorecards: {
    title: "Judged Scorecards",
    path: "/judged-scorecards",
  },
  volunteerManagement: {
    title: "Volunteer Management",
    path: "/volunteer-management",
  },
  sponsorManagement: {
    title: "Sponsor Management",
    path: "/sponsor-management",
  },
};

// =====================================================
// 🔎 Public Page SEO
// Purpose:
// - Central SEO metadata for non-event public pages
// - Used by homepage, request page, terms, about, privacy, contact, pricing, etc.
// - Event-specific pages can still use dynamic SEO based on Firestore data
// =====================================================

export const PUBLIC_PAGE_SEO = {
  home: {
    title: "QR Code Voting for Events | Let's Go Vote",
    description:
      "Let's Go Vote provides QR code voting, People's Choice voting, ranked choice voting, judged scorecards, silent auctions, volunteer management, and sponsor tools for real-world events.",
    path: "/",
    image: "https://letsgovote.app/images/social-share-default.png",
    breadcrumbs: [
      { name: "Home", path: "/" },
    ],
  },
  requestEvent: {
    title: "Request an Event | Let's Go Vote",
    description:
      "Request Let's Go Vote for your car show, art show, pet contest, school election, silent auction, fundraiser, or community event.",
    path: "/request_event",
    image: "https://letsgovote.app/images/social-share-default.png",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Request Event", path: "/request_event" },
    ],
  },
  terms: {
    title: "Terms & Conditions | Let's Go Vote",
    description:
      "Read the Let's Go Vote terms and conditions for event voting, accounts, voting integrity, volunteer activities, auctions, and participation.",
    path: "/terms",
    image: "https://letsgovote.app/images/social-share-default.png",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Terms", path: "/terms" },
    ],
  },
  solutions: {
    title: "Event Voting Solutions | Let's Go Vote",
    description:
      "Explore Let's Go Vote solutions for car shows, art shows, pet shows, school elections, silent auctions, and QR code event voting.",
    path: "/solutions",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Solutions", path: "/solutions" },
    ],
  },
  howItWorks: {
    title: "How Event Voting Works | Let's Go Vote",
    description:
      "Learn how Let's Go Vote helps organizers run QR code voting, People's Choice awards, ranked choice voting, silent auctions, volunteer management, and sponsor programs.",
    path: "/how-it-works",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "How It Works", path: "/how-it-works" },
    ],
  },
  about: {
    title: "About Let's Go Vote | Event Voting Software",
    description:
      "Learn about Let's Go Vote, a QR code voting platform built for car shows, art shows, pet contests, school elections, silent auctions, sponsors, and volunteers.",
    path: "/about",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "About", path: "/about" },
    ],
  },
  contact: {
    title: "Contact Let's Go Vote | Event Voting Support",
    description:
      "Contact Let's Go Vote for help with QR code voting, event voting setup, silent auctions, sponsors, volunteers, and event requests.",
    path: "/contact",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Contact", path: "/contact" },
    ],
  },
  privacy: {
    title: "Privacy Policy | Let's Go Vote",
    description:
      "Read the Let's Go Vote privacy policy and learn how event, voter, volunteer, sponsor, and contact form data is handled.",
    path: "/privacy",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Privacy", path: "/privacy" },
    ],
  },
  volunteering: {
    title: "Volunteer Opportunities | Let's Go Vote",
    description:
      "Browse volunteer opportunities and sign up for event activities using Let's Go Vote volunteer management tools.",
    path: "/volunteering",
    image:
      "https://letsgovote.app/images/social-share-volunteer-management.png",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Volunteering", path: "/volunteering" },
    ],
  },
  login: {
    title: "Login | Let's Go Vote",
    description:
      "Log in to Let's Go Vote to vote in events, manage participation, access ballots, and use event tools.",
    path: "/login",
    image: "https://letsgovote.app/images/social-share-default.png",
    noindex: true,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Login", path: "/login" },
    ],
  }, 
  register: {
    title: "Create an Account | Let's Go Vote",
    description:
      "Create a Let's Go Vote account to vote in events, volunteer, participate in auctions, and manage event activities.",
    path: "/register",
    image: "https://letsgovote.app/images/social-share-default.png",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Register", path: "/register" },
    ],
  },
  volunteerOpportunities: {
    title: "Volunteer Opportunities for Events | Let's Go Vote",
    description:
      "Find volunteer activities, schedules, and opportunities at participating Let's Go Vote events.",
    path: "/volunteer_opportunities",
    image:
      "https://letsgovote.app/images/social-share-volunteer-management.png",
    breadcrumbs: [
      { name: "Home", path: "/" },
      {
        name: "Volunteer Opportunities",
        path: "/volunteer_opportunities",
      },
    ],
  },
};
